import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Eye, BookOpen } from 'lucide-react';
import { CTAButton } from './CTAButton';
const screenshots = [{
  url: 'https://cdn.magicpatterns.com/uploads/q7A3nLqyZcjRowHN394gjX/capturas_del_ebook_por_dentro1.png',
  hook: 'Aprende a manejar el tiempo de pantallas sin culpa',
  alt: 'Captura del ebook: Manejo de pantallas'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/5ZPimA97H4HfGT5sfwkjgx/capturas_del_ebook_por_dentro2.png',
  hook: 'Filosofía Montessori explicada de forma simple',
  alt: 'Captura del ebook: Filosofía Montessori'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/2Zs69c4TdRGnQpyA4qnHDV/capturas_del_ebook_por_dentro3.png',
  hook: 'Actividades creativas paso a paso para cada edad',
  alt: 'Captura del ebook: Actividades creativas'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/oMimy3b3MbCpE24EUfbJae/capturas_del_ebook_por_dentro4.png',
  hook: 'Juegos cognitivos que desarrollan habilidades reales',
  alt: 'Captura del ebook: Juegos cognitivos'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/vymbGreCrSHmEFdK1szcGX/capturas_del_ebook_por_dentro5.png',
  hook: 'Instrucciones claras con materiales de casa',
  alt: 'Captura del ebook: Instrucciones claras'
}];
export function EbookPreviewSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % screenshots.length);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + screenshots.length) % screenshots.length);
  };
  return <section className="py-20 px-4 bg-white overflow-hidden relative" data-id="element-76">
      <div className="max-w-6xl mx-auto" data-id="element-77">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-78">
          <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-600 px-4 py-2 rounded-full font-bold text-sm md:text-base mb-6 animate-pulse shadow-sm" data-id="element-79">
            <Eye className="w-5 h-5" data-id="element-80" />
            VISTA PREVIA EXCLUSIVA
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-6 leading-tight" data-id="element-81">
            👀 Mira lo que encontrarás{' '}
            <span className="text-pink-500 underline decoration-wavy" data-id="element-82">
              dentro...
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-medium" data-id="element-83">
            Este no es otro ebook aburrido. Es una guía práctica, visual y llena
            de color diseñada para que disfruten cada página.
          </p>
        </div>

        {/* Gallery Container */}
        <div className="relative max-w-5xl mx-auto mb-16" data-id="element-84">
          {/* Main Carousel */}
          <div className="relative h-[350px] md:h-[550px] flex items-center justify-center perspective-1000" data-id="element-85">
            <AnimatePresence mode="popLayout" data-id="element-86">
              {screenshots.map((screenshot, index) => {
              const offset = (index - currentIndex + screenshots.length) % screenshots.length;
              const isCenter = offset === 0;
              const isLeft = offset === screenshots.length - 1;
              const isRight = offset === 1;
              if (!isCenter && !isLeft && !isRight) return null;
              return <motion.div key={screenshot.url} initial={{
                opacity: 0,
                scale: 0.8,
                x: isRight ? 100 : isLeft ? -100 : 0
              }} animate={{
                opacity: 1,
                scale: isCenter ? 1 : 0.85,
                x: isCenter ? 0 : isRight ? '50%' : '-50%',
                zIndex: isCenter ? 10 : 5,
                rotateY: isCenter ? 0 : isRight ? -15 : 15,
                filter: isCenter ? 'blur(0px)' : 'blur(2px)'
              }} exit={{
                opacity: 0,
                scale: 0.8
              }} transition={{
                duration: 0.5,
                ease: 'easeOut'
              }} className="absolute w-[85%] md:w-[60%] max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden border-8 border-white" data-id="element-87">
                    <div className="relative aspect-[4/3] md:aspect-[16/10]" data-id="element-88">
                      <img src={screenshot.url} alt={screenshot.alt} className="w-full h-full object-cover" data-id="element-89" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-80" data-id="element-90" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white text-center" data-id="element-91">
                        <h3 className="text-lg md:text-2xl font-bold leading-tight drop-shadow-lg" data-id="element-92">
                          {screenshot.hook}
                        </h3>
                      </div>
                    </div>
                  </motion.div>;
            })}
            </AnimatePresence>

            {/* Navigation Buttons */}
            <button onClick={prevSlide} className="absolute left-2 md:left-0 z-20 bg-white hover:bg-gray-50 text-gray-800 p-3 rounded-full shadow-lg border-2 border-gray-100 transition-all hover:scale-110" data-id="element-93">
              <ChevronLeft className="w-6 h-6" data-id="element-94" />
            </button>
            <button onClick={nextSlide} className="absolute right-2 md:right-0 z-20 bg-white hover:bg-gray-50 text-gray-800 p-3 rounded-full shadow-lg border-2 border-gray-100 transition-all hover:scale-110" data-id="element-95">
              <ChevronRight className="w-6 h-6" data-id="element-96" />
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-3 mt-8" data-id="element-97">
            {screenshots.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-pink-500 w-8' : 'bg-gray-300 hover:bg-pink-300'}`} data-id="element-98" />)}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center bg-pink-50 p-8 rounded-[2rem] shadow-lg border-2 border-pink-100 max-w-3xl mx-auto" data-id="element-99">
          <BookOpen className="w-12 h-12 text-pink-500 mx-auto mb-4" data-id="element-100" />
          <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-101">
            ¿Listo para ver el resto?
          </h3>
          <p className="text-gray-600 mb-8 font-medium" data-id="element-102">
            Esto es solo una pequeña muestra de las más de 500 actividades que
            te esperan dentro del Mega Pack.
          </p>
          <CTAButton text="¡QUIERO VER EL EBOOK COMPLETO!" className="w-full md:w-auto shadow-pink-500/30" data-id="element-103" />
        </div>
      </div>
    </section>;
}